import { HeartHandshake } from "lucide-react";
import PageHero from "@/components/PageHero";
import CTASection from "@/components/CTASection";
import { Chapter, Reveal } from "@/components/motion";
import { IMAGES } from "@/data/site";

const DEVELOPMENT = [
  "Skills development",
  "Site-specific requirements",
  "Safety awareness",
  "Professional workplace conduct",
  "Ongoing workforce support",
  "Client feedback",
  "Continuous performance improvement",
];

export default function Workforce() {
  return (
    <div data-testid="workforce-page">
      <PageHero
        index="03"
        label="Our Workforce"
        titleLines={["People Selected", { t: "for Performance.", c: "text-gold" }]}
        intro="The quality of a labour hire company is ultimately determined by the quality of its workforce. At Formcore, personnel are carefully selected according to capability, experience, reliability and suitability for the working environment."
        image={IMAGES.workforce}
      />

      <section className="bg-white py-28 lg:py-32">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid gap-14 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <Chapter index="01" label="Our Approach" />
              <Reveal delay={0.1}>
                <h2 className="mt-8 font-heading text-3xl font-extrabold leading-[1.1] tracking-tight text-navy sm:text-4xl">
                  No One-Size-Fits-All. <span className="text-gold">The Right Match.</span>
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-7 lg:pt-24">
              <Reveal delay={0.15}>
                <p className="text-base leading-relaxed text-charcoal/75">
                  We do not believe in a one-size-fits-all approach. Personnel are matched to the requirements of the
                  role, project and client wherever possible — selected with consideration to capability, experience,
                  reliability and project requirements.
                </p>
                <p className="mt-5 text-base leading-relaxed text-charcoal/75">
                  Our objective is to provide personnel who meet the expectations of our clients and contribute
                  positively to project outcomes.
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-surface py-28 lg:py-32">
        <div className="mx-auto max-w-7xl px-6">
          <Chapter index="02" label="Continuous Development" />
          <div className="mt-8 grid gap-14 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <Reveal delay={0.1}>
                <h2 className="font-heading text-3xl font-extrabold leading-[1.1] tracking-tight text-navy sm:text-4xl">
                  Continuously Developing <span className="text-gold">Our Workforce</span>
                </h2>
                <p className="mt-6 text-base leading-relaxed text-charcoal/75">
                  Formcore is committed to continuously developing its workforce. We invest in training and upskilling
                  to improve workforce capability and ensure our personnel are equipped to meet the evolving
                  requirements of the construction industry.
                </p>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <div className="border-t border-navy/10">
                {DEVELOPMENT.map((item, i) => (
                  <Reveal key={item} delay={i * 0.05} y={16}>
                    <div
                      className="group flex items-center gap-6 border-b border-navy/10 py-5 transition-colors hover:border-gold"
                      data-testid={`development-item-${i}`}
                    >
                      <span className="font-heading text-sm font-bold text-gold">{String(i + 1).padStart(2, "0")}</span>
                      <span className="font-heading text-lg font-bold text-navy transition-transform duration-300 group-hover:translate-x-1.5">
                        {item}
                      </span>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-navy py-28 lg:py-32">
        <div className="absolute inset-0 bg-blueprint-dark opacity-30" />
        <div className="relative z-10 mx-auto max-w-7xl px-6">
          <div className="grid items-center gap-16 lg:grid-cols-2">
            <Reveal className="relative order-2 lg:order-1">
              <div className="absolute -left-5 -top-5 hidden h-full w-full border-2 border-gold/40 md:block" />
              <img
                src={IMAGES.workforce}
                alt="Formcore personnel in PPE on site"
                className="clip-frame relative h-[300px] w-full object-cover md:h-[440px]"
              />
            </Reveal>
            <div className="order-1 lg:order-2">
              <Chapter index="03" label="Worker Welfare" dark />
              <Reveal delay={0.1}>
                <h2 className="mt-8 font-heading text-3xl font-extrabold leading-[1.1] tracking-tight text-white sm:text-4xl">
                  Supported from <span className="text-gold">Site to Home</span>
                </h2>
              </Reveal>
              <Reveal delay={0.2}>
                <p className="mt-6 text-base leading-relaxed text-white/70">
                  Our responsibility extends beyond simply placing a worker on a site. Formcore places strong emphasis
                  on the welfare and wellbeing of its workforce, including appropriate social, financial and
                  logistical support.
                </p>
                <p className="mt-5 text-base leading-relaxed text-white/70">
                  We believe that workers who are properly supported are better positioned to perform, remain engaged
                  and contribute positively to the projects on which they work.
                </p>
                <p className="mt-8 flex items-center gap-3 font-heading text-sm font-bold uppercase tracking-[0.15em] text-gold">
                  <HeartHandshake className="h-5 w-5" />
                  Supported workers. Stronger projects.
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <CTASection title="Need Reliable, Carefully Selected Personnel?" />
    </div>
  );
}
